import { useState } from 'react';
import { SideDrawer, withStyles } from 'vopay';
import { Button } from 'UI';
import { useTranslation } from 'react-i18next';
import { useBlockingLoader } from 'shared';
import useCreatePartner from '@/hooks/useApi/useCreatePartner';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useToast } from '@/providers/Toast.provider';
import usePartnerCreationFormConfig, {
  SerializedPartnerCreationValues,
} from '@/components/Partners/PartnerCreation/usePartnerCreationFormConfig';
import PartnerCreationDrawerForm, { PARTNER_CREATION_FORM_ID } from './PartnerCreationDrawerForm';
import styles from './PartnerCreationDrawer.module.scss';

const cn = withStyles(styles);

// The page mounts the drawer only while it is open, so there is no isOpen prop — mounting is what
// opens it.
type PartnerCreationDrawerProps = {
  onClose: () => void;
  // Called after a successful creation, before onClose, so the page can refresh its list.
  onCreated: () => void;
};

export default function PartnerCreationDrawer({ onClose, onCreated }: PartnerCreationDrawerProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Partners.Create' });
  const { creating, createPartner } = useCreatePartner();
  const alertDialog = useAlertDialog();
  const toast = useToast();
  const blockingLoader = useBlockingLoader();
  const [reviewing, setReviewing] = useState(false);

  async function handleSubmit(values: SerializedPartnerCreationValues): Promise<void> {
    blockingLoader.show();

    try {
      await createPartner({
        Name: values.PartnerName,
        EmailAddress: values.EmailAddress,
        Country: values.Country,
        PartnerAccountID: values.PartnerAccountID || undefined,
      });
    } catch (error) {
      // Return to edit mode so the user can correct the input and retry.
      setReviewing(false);

      alertDialog.error(
        t('errorTitle', { defaultValue: 'Unable to Create Partner' }),
        error instanceof Error && error.message
          ? error.message
          : t('errorMessage', { defaultValue: 'Something went wrong. Please try again.' }),
      );

      return;
    } finally {
      blockingLoader.hide();
    }

    toast.success(
      t('successMessage', {
        defaultValue: 'The partner has been created and entered the underwriting process.',
      }),
    );

    onCreated();
    onClose();
  }

  // Esc / the close button must not dismiss the drawer while the create request is in flight —
  // the success path closes it itself once the request settles.
  const handleClose = () => {
    if (!creating) onClose();
  };

  // The form config lives here (not in the form) so the drawer footer can read validity and
  // submit the form from outside it.
  const { ctx, renderFormElements, businessInfoConfig } = usePartnerCreationFormConfig({
    onSubmit: handleSubmit,
    isReviewing: reviewing,
  });

  return (
    <SideDrawer
      isOpen
      onClose={handleClose}
      title={t('pageTitle', { defaultValue: 'Add Partner Account' })}
      footer={
        <div className={cn('footer')}>
          {reviewing ? (
            <>
              <Button type="button" onClick={() => setReviewing(false)} disabled={creating}>
                {t('backButton', { defaultValue: 'Back' })}
              </Button>

              <Button
                type="submit"
                form={PARTNER_CREATION_FORM_ID}
                colorScheme="primary"
                disabled={!ctx.isValid || creating}
              >
                {t('confirmButton', { defaultValue: 'Confirm' })}
              </Button>
            </>
          ) : (
            <Button type="button" colorScheme="primary" disabled={!ctx.isValid} onClick={() => setReviewing(true)}>
              {t('reviewButton', { defaultValue: 'Review' })}
            </Button>
          )}
        </div>
      }
    >
      <PartnerCreationDrawerForm
        ctx={ctx}
        renderFormElements={renderFormElements}
        businessInfoConfig={businessInfoConfig}
      />
    </SideDrawer>
  );
}
